import { recordingBufferService } from '../services/recordingBufferService';

/**
 * Subscription-entitlement block carried on the stored user (from user_context)
 * and the JWT. Boolean feature flags gate UI surfaces; session_limit is the
 * per-doctor monthly cap (null = unlimited); plan_slug identifies the plan.
 */
export interface Entitlements {
  pre_consultation?: boolean;
  referral_letter_generation?: boolean;
  automated_workflows?: boolean;
  pdf_ingestion?: boolean;
  vbc_analytics?: boolean;
  chroniq?: boolean;
  patient_reported_outcomes?: boolean;
  care_gaps?: boolean;
  session_limit?: number | null;
  plan_slug?: string | null;
  // Whether the clinic's billing currently grants access (active/trialing, or
  // past_due within grace). Drives the "subscription pending" dashboard block
  // for medical officers. Absent on tokens issued before this flag existed —
  // treated as active by `isSubscriptionActive` so rollout never locks anyone out.
  subscription_active?: boolean;
  // Forward-compatible: tolerate additional keys the backend may add.
  [key: string]: boolean | number | string | null | undefined;
}

interface StorageData {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  user: {
    name: string;
    email: string;
    phone_no: string;
    age: string;
    gender: string;
    country: string;
    specialty?: string;
    specialties?: string[];
    clinic_id?: number;
    clinic_name?: string;
    is_admin: boolean;
    is_super_admin: boolean;
    is_patient: boolean;
    is_medical_officer: boolean;
    // Nursing staff. Optional because tokens issued before the auth service
    // gained the claim carry no `is_nurse` at all — absent means "not a nurse",
    // never `undefined` in a comparison (see types/staffRoles).
    is_nurse?: boolean;
    is_approved?: boolean;
    is_root_admin?: boolean;
    is_clinic_staff?: boolean;
    // Set when a super admin is acting-as a specific medical officer in a clinic.
    // Doctor-scoped UI (sessions list default, headers, writes) uses these.
    acting_as_doctor_id?: number;
    acting_as_doctor_name?: string;
    // Subscription-derived feature entitlements, copied verbatim from the auth
    // backend's user_context.entitlements. Feature flags are booleans; session_limit
    // is a number|null (null = unlimited); plan_slug is a string|null. Super-admin
    // sessions have no entitlements and are treated as full access by the helper.
    entitlements?: Entitlements;
  };
  registration_status?: {
    is_verified: number;
    step_1_password: number;
    step_2_mfa: number;
    step_3_clinic: number;
    step_4_subscription: number;
    step_5_medical_officers: number;
    current_step: number | null;
    is_registration_complete: boolean;
    completion_percentage: number;
    user_id: number;
    user_email: string;
    clinic_id?: number;
    clinic_is_approved?: boolean;
    approval_status?: 'pending' | 'approved' | 'rejected';
  };
}

const AUTH_KEY = 'auth_data';
const PENDING_AUTH_KEY = 'pending_auth_data';
const DISCLAIMER_KEY_PREFIX = 'disclaimer_accepted_';
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';
const DISCLAIMER_SHOWN_KEY = 'disclaimerShown';
const SCRIBE_RUN_KEY_PREFIX = 'scribe_run_';

/**
 * Bookkeeping for an in-flight per-agent scribe run, so a reload mid-run can
 * resume it. Holds only the run (analysis) id and its start time — no PHI.
 */
export interface StoredScribeRun {
  analysisId: number;
  startedAt: number;
}

export const storage = {
  setAuthData(data: StorageData) {
    localStorage.setItem(AUTH_KEY, JSON.stringify(data));
    this.updateLastActivity();
    window.dispatchEvent(new Event('auth-data-changed'));
  },

  getAuthData(): StorageData | null {
    const data = localStorage.getItem(AUTH_KEY);
    return data ? JSON.parse(data) : null;
  },

  clearAuthData() {
    localStorage.removeItem(AUTH_KEY);
    window.dispatchEvent(new Event('auth-data-changed'));
  },

  // Pending auth data methods (sessionStorage for temporary storage)
  setPendingAuthData(data: {
    access_token: string;
    refresh_token: string;
    user_context: any;
    email: string;
  }) {
    sessionStorage.setItem(PENDING_AUTH_KEY, JSON.stringify(data));
  },

  getPendingAuthData(): {
    access_token: string;
    refresh_token: string;
    user_context: any;
    email: string;
  } | null {
    const data = sessionStorage.getItem(PENDING_AUTH_KEY);
    return data ? JSON.parse(data) : null;
  },

  clearPendingAuthData() {
    sessionStorage.removeItem(PENDING_AUTH_KEY);
  },

  // Disclaimer acceptance methods (sessionStorage - only for current session)
  setDisclaimerAccepted(email: string) {
    const key = `${DISCLAIMER_KEY_PREFIX}${email}`;
    sessionStorage.setItem(key, 'true');
  },

  isDisclaimerAccepted(email: string): boolean {
    const key = `${DISCLAIMER_KEY_PREFIX}${email}`;
    return sessionStorage.getItem(key) === 'true';
  },

  clearDisclaimerAccepted(email: string) {
    const key = `${DISCLAIMER_KEY_PREFIX}${email}`;
    sessionStorage.removeItem(key);
  },

  getAccessToken(): string | null {
    const data = this.getAuthData();
    return data ? data.access_token : null;
  },

  getRefreshToken(): string | null {
    const data = this.getAuthData();
    return data ? data.refresh_token : null;
  },

  getUser() {
    const data = this.getAuthData();
    return data ? data.user : null;
  },

  getUserEmail(): string | null {
    const data = this.getAuthData();
    if (!data) return null;
    
    // Try to get email from registration_status first, then from user object
    return data.registration_status?.user_email || data.user.email || null;
  },

  setThreadId(threadId: string) {
    localStorage.setItem('thread_id', threadId);
  },

  getThreadId(): string | null {
    return localStorage.getItem('thread_id');
  },

  clearThreadId() {
    localStorage.removeItem('thread_id');
  },

  // Clear all application storage data including PHI
  clearAll() {
    this.clearAuthData();
    this.clearThreadId();
    this.clearLastActivity();

    // Clear all PHI data from sessionStorage
    sessionStorage.clear();

    // Clear any legacy PHI data that may still be in localStorage
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('transcript_') ||
        key.startsWith('session_') ||
        key.startsWith('transcript_segments_') ||
        key.startsWith('private_notes_') ||
        key.startsWith('certificate_') ||
        key.startsWith('referral_') ||
        key.startsWith('signature_') ||
        key.startsWith('digital_signature')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear IndexedDB recording data (PHI)
    recordingBufferService.clearAll().catch(() => {});
  },

  // Transcript storage functions for real-time transcription (sessionStorage — PHI)
  setTranscript(sessionId: number, transcript: string) {
    const key = `transcript_${sessionId}`;
    const transcriptData = {
      sessionId,
      transcript,
      timestamp: Date.now(),
      lastUpdated: new Date().toISOString()
    };
    sessionStorage.setItem(key, JSON.stringify(transcriptData));
  },

  getTranscript(sessionId: number): string | null {
    const key = `transcript_${sessionId}`;
    const data = sessionStorage.getItem(key);
    if (data) {
      try {
        const transcriptData = JSON.parse(data);
        return transcriptData.transcript || null;
      } catch (error) {
        console.error('Error parsing stored transcript:', error);
        return null;
      }
    }
    return null;
  },

  appendToTranscript(sessionId: number, newText: string) {
    const existingTranscript = this.getTranscript(sessionId) || '';
    const updatedTranscript = existingTranscript ? `${existingTranscript} ${newText}` : newText;
    this.setTranscript(sessionId, updatedTranscript);
  },

  clearTranscript(sessionId: number) {
    const key = `transcript_${sessionId}`;
    sessionStorage.removeItem(key);
  },

  // Get all transcript keys from sessionStorage (for cleanup if needed)
  getAllTranscriptKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('transcript_')) {
        keys.push(key);
      }
    }
    return keys;
  },

  // Clean up old transcripts (optional utility)
  cleanupOldTranscripts(maxAgeInDays: number = 7) {
    const maxAge = maxAgeInDays * 24 * 60 * 60 * 1000; // Convert to milliseconds
    const now = Date.now();

    this.getAllTranscriptKeys().forEach(key => {
      const data = sessionStorage.getItem(key);
      if (data) {
        try {
          const transcriptData = JSON.parse(data);
          if (transcriptData.timestamp && (now - transcriptData.timestamp) > maxAge) {
            sessionStorage.removeItem(key);
          }
        } catch (error) {
          // Remove corrupted data
          sessionStorage.removeItem(key);
          // eslint-disable-next-line no-console
          console.warn(`Removed corrupted transcript data: ${key}`);
        }
      }
    });
  },

  // Last activity timestamp (used by InactivityDetector and setAuthData)
  getLastActivity(): number | null {
    const val = localStorage.getItem(LAST_ACTIVITY_KEY);
    return val ? parseInt(val, 10) : null;
  },

  updateLastActivity() {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  },

  clearLastActivity() {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  },

  // Session draft auto-save (assessment + plan) — sessionStorage for PHI
  setSessionDraft(sessionId: string | number, assessment: string, plan: string) {
    sessionStorage.setItem(`session_${sessionId}_assessment`, assessment);
    sessionStorage.setItem(`session_${sessionId}_plan`, plan);
  },

  getSessionDraft(sessionId: string | number): { assessment: string | null; plan: string | null } {
    return {
      assessment: sessionStorage.getItem(`session_${sessionId}_assessment`),
      plan: sessionStorage.getItem(`session_${sessionId}_plan`),
    };
  },

  // Active per-agent scribe run (sessionStorage — cleared with the rest on logout)
  setScribeRun(sessionId: number, run: StoredScribeRun) {
    sessionStorage.setItem(`${SCRIBE_RUN_KEY_PREFIX}${sessionId}`, JSON.stringify(run));
  },

  getScribeRun(sessionId: number): StoredScribeRun | null {
    const raw = sessionStorage.getItem(`${SCRIBE_RUN_KEY_PREFIX}${sessionId}`);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.analysisId === 'number' && typeof parsed?.startedAt === 'number') {
        return { analysisId: parsed.analysisId, startedAt: parsed.startedAt };
      }
    } catch {
      // Corrupted entry — drop it below
    }
    sessionStorage.removeItem(`${SCRIBE_RUN_KEY_PREFIX}${sessionId}`);
    return null;
  },

  clearScribeRun(sessionId: number) {
    sessionStorage.removeItem(`${SCRIBE_RUN_KEY_PREFIX}${sessionId}`);
  },

  // In-consult fallback data — sessionStorage for PHI
  getInConsultFallback(sessionId: string | number) {
    return {
      history: sessionStorage.getItem(`session_${sessionId}_inconsult_history`),
      ros: sessionStorage.getItem(`session_${sessionId}_inconsult_ros`),
      exam: sessionStorage.getItem(`session_${sessionId}_inconsult_exam`),
    };
  },

  // Disclaimer shown (sessionStorage — tracks whether disclaimer was displayed this browser session)
  isDisclaimerShown(): boolean {
    return sessionStorage.getItem(DISCLAIMER_SHOWN_KEY) === 'true';
  },

  setDisclaimerShown() {
    sessionStorage.setItem(DISCLAIMER_SHOWN_KEY, 'true');
  },

  clearDisclaimerShown() {
    sessionStorage.removeItem(DISCLAIMER_SHOWN_KEY);
  },

  // PHI-safe sessionStorage wrappers for clinical data
  setPhiItem(key: string, value: string) {
    sessionStorage.setItem(key, value);
  },

  getPhiItem(key: string): string | null {
    return sessionStorage.getItem(key);
  },

  removePhiItem(key: string) {
    sessionStorage.removeItem(key);
  },

  // Generic localStorage wrapper methods (non-PHI only)
  setItem(key: string, value: string) {
    localStorage.setItem(key, value);
  },

  getItem(key: string): string | null {
    return localStorage.getItem(key);
  },

  removeItem(key: string) {
    localStorage.removeItem(key);
  }

};
